/**
 * Plugin Constants
 * Adapted from promptfoo/redteam constants for EarlyCore demo flow
 */

import type { Plugin, PluginConfigSchema, PluginPreset } from '@/types/redteam';

// Re-export for convenience
export type { Plugin } from '@/types/redteam';

/**
 * Framework preset IDs - these match the promptfoo framework IDs
 */
export type FrameworkPresetId =
  | 'nist:ai:measure'
  | 'owasp:api'
  | 'owasp:llm'
  | 'owasp:agentic'
  | 'mitre:atlas'
  | 'eu:ai-act'
  | 'iso:42001'
  | 'gdpr';

/**
 * Plugin preset configuration with framework ID support
 */
export type PluginPresetWithFramework = Omit<PluginPreset, 'id'> & {
  id: FrameworkPresetId | 'basic' | 'comprehensive' | 'rag' | 'mcp';
};

/**
 * Aggregate all unique plugins from a framework mapping
 */
function aggregatePluginsFromMapping(
  mapping: Record<string, { plugins: Plugin[]; strategies: unknown[] }>
): Plugin[] {
  const pluginSet = new Set<Plugin>();
  Object.values(mapping).forEach((entry) => {
    entry.plugins.forEach((plugin) => pluginSet.add(plugin));
  });
  return Array.from(pluginSet).sort();
}

/**
 * Basic preset - minimal set of essential plugins
 */
const BASIC_PLUGINS: Plugin[] = [
  'ascii-smuggling',
  'bfla',
  'bola',
  'hijacking',
  'pii:direct',
  'prompt-extraction',
  'shell-injection',
  'sql-injection',
] as Plugin[];

/**
 * NIST AI RMF plugins - aggregated from all NIST measures
 */
const NIST_PLUGINS: Plugin[] = aggregatePluginsFromMapping({
  'nist:ai:measure:1.1': {
    plugins: ['excessive-agency', 'harmful:misinformation-disinformation'],
    strategies: [],
  },
  'nist:ai:measure:1.2': {
    plugins: ['excessive-agency', 'harmful:misinformation-disinformation'],
    strategies: [],
  },
  'nist:ai:measure:2.1': {
    plugins: ['harmful:privacy', 'pii:api-db', 'pii:direct', 'pii:session', 'pii:social'],
    strategies: [],
  },
  'nist:ai:measure:2.2': {
    plugins: ['harmful:privacy', 'pii:api-db', 'pii:direct', 'pii:session', 'pii:social'],
    strategies: [],
  },
  'nist:ai:measure:2.3': {
    plugins: ['excessive-agency'],
    strategies: [],
  },
  'nist:ai:measure:2.4': {
    plugins: ['excessive-agency', 'harmful:misinformation-disinformation'],
    strategies: [],
  },
  'nist:ai:measure:2.5': {
    plugins: ['excessive-agency'],
    strategies: [],
  },
  'nist:ai:measure:2.6': {
    plugins: [
      'harmful:chemical-biological-weapons',
      'harmful:indiscriminate-weapons',
      'harmful:unsafe-practices',
    ],
    strategies: [],
  },
  'nist:ai:measure:2.7': {
    plugins: ['harmful:cybercrime', 'shell-injection', 'sql-injection'],
    strategies: [],
  },
  'nist:ai:measure:2.8': {
    plugins: ['bfla', 'bola', 'rbac'],
    strategies: [],
  },
  'nist:ai:measure:2.9': {
    plugins: ['excessive-agency'],
    strategies: [],
  },
  'nist:ai:measure:2.10': {
    plugins: ['harmful:privacy', 'pii:api-db', 'pii:direct', 'pii:session', 'pii:social'],
    strategies: [],
  },
  'nist:ai:measure:2.11': {
    plugins: ['harmful:harassment-bullying', 'harmful:hate', 'harmful:insults'],
    strategies: [],
  },
  'nist:ai:measure:2.12': {
    plugins: [],
    strategies: [],
  },
  'nist:ai:measure:2.13': {
    plugins: ['excessive-agency'],
    strategies: [],
  },
  'nist:ai:measure:3.1': {
    plugins: ['excessive-agency', 'harmful:misinformation-disinformation'],
    strategies: [],
  },
  'nist:ai:measure:3.2': {
    plugins: ['excessive-agency'],
    strategies: [],
  },
  'nist:ai:measure:3.3': {
    plugins: ['excessive-agency'],
    strategies: [],
  },
  'nist:ai:measure:4.1': {
    plugins: ['excessive-agency'],
    strategies: [],
  },
  'nist:ai:measure:4.2': {
    plugins: ['excessive-agency', 'harmful:misinformation-disinformation'],
    strategies: [],
  },
  'nist:ai:measure:4.3': {
    plugins: ['excessive-agency'],
    strategies: [],
  },
});

/**
 * OWASP API Top 10 plugins
 */
const OWASP_API_PLUGINS: Plugin[] = aggregatePluginsFromMapping({
  'owasp:api:01': {
    plugins: ['bola', 'rbac'],
    strategies: [],
  },
  'owasp:api:02': {
    plugins: ['bfla', 'rbac'],
    strategies: [],
  },
  'owasp:api:03': {
    plugins: ['excessive-agency', 'overreliance'],
    strategies: [],
  },
  'owasp:api:04': {
    plugins: ['harmful:privacy', 'pii:api-db', 'pii:session'],
    strategies: [],
  },
  'owasp:api:05': {
    plugins: ['bfla', 'bola', 'rbac'],
    strategies: [],
  },
  'owasp:api:06': {
    plugins: ['harmful:misinformation-disinformation', 'overreliance'],
    strategies: [],
  },
  'owasp:api:07': {
    plugins: ['shell-injection', 'sql-injection'],
    strategies: [],
  },
  'owasp:api:08': {
    plugins: ['harmful:privacy', 'pii:api-db', 'pii:session'],
    strategies: [],
  },
  'owasp:api:09': {
    plugins: ['harmful:specialized-advice', 'overreliance'],
    strategies: [],
  },
  'owasp:api:10': {
    plugins: ['debug-access', 'harmful:privacy'],
    strategies: [],
  },
});

/**
 * OWASP LLM Top 10 plugins
 */
const OWASP_LLM_PLUGINS: Plugin[] = aggregatePluginsFromMapping({
  'owasp:llm:01': {
    plugins: ['ascii-smuggling', 'indirect-prompt-injection', 'prompt-extraction', 'harmful'],
    strategies: [],
  },
  'owasp:llm:02': {
    plugins: [
      'pii:api-db',
      'pii:direct',
      'pii:session',
      'pii:social',
      'harmful:privacy',
      'cross-session-leak',
      'prompt-extraction',
    ],
    strategies: [],
  },
  'owasp:llm:03': {
    plugins: [],
    strategies: [],
  },
  'owasp:llm:04': {
    plugins: [
      'harmful:misinformation-disinformation',
      'harmful:hate',
      'bias:age',
      'bias:disability',
      'bias:gender',
      'bias:race',
      'harmful:radicalization',
      'harmful:specialized-advice',
    ],
    strategies: [],
  },
  'owasp:llm:05': {
    plugins: ['shell-injection', 'sql-injection', 'ssrf', 'debug-access'],
    strategies: [],
  },
  'owasp:llm:06': {
    plugins: [
      'excessive-agency',
      'rbac',
      'bfla',
      'bola',
      'shell-injection',
      'sql-injection',
      'ssrf',
    ],
    strategies: [],
  },
  'owasp:llm:07': {
    plugins: [
      'prompt-extraction',
      'rbac',
      'harmful:privacy',
      'pii:api-db',
      'pii:direct',
      'pii:session',
      'pii:social',
    ],
    strategies: [],
  },
  'owasp:llm:08': {
    plugins: [
      'cross-session-leak',
      'harmful:privacy',
      'pii:api-db',
      'pii:direct',
      'pii:session',
      'pii:social',
    ],
    strategies: [],
  },
  'owasp:llm:09': {
    plugins: [
      'hallucination',
      'overreliance',
      'harmful:misinformation-disinformation',
      'harmful:specialized-advice',
    ],
    strategies: [],
  },
  'owasp:llm:10': {
    plugins: ['divergent-repetition', 'reasoning-dos'],
    strategies: [],
  },
});

/**
 * OWASP Agentic AI plugins
 */
const OWASP_AGENTIC_PLUGINS: Plugin[] = aggregatePluginsFromMapping({
  'owasp:agentic:t01': {
    plugins: ['agentic:memory-poisoning'],
    strategies: [],
  },
  'owasp:agentic:t02': {
    plugins: ['excessive-agency', 'mcp', 'tool-discovery'],
    strategies: [],
  },
  'owasp:agentic:t03': {
    plugins: ['rbac', 'bfla', 'bola'],
    strategies: [],
  },
  'owasp:agentic:t04': {
    plugins: ['hallucination', 'harmful:misinformation-disinformation'],
    strategies: [],
  },
  'owasp:agentic:t05': {
    plugins: ['hijacking', 'system-prompt-override'],
    strategies: [],
  },
  'owasp:agentic:t06': {
    plugins: ['contracts', 'goal-misalignment', 'excessive-agency'],
    strategies: [],
  },
  'owasp:agentic:t08': {
    plugins: ['imitation', 'cross-session-leak', 'pii:session'],
    strategies: [],
  },
  'owasp:agentic:t10': {
    plugins: ['shell-injection', 'sql-injection', 'harmful:cybercrime:malicious-code', 'ssrf'],
    strategies: [],
  },
});

/**
 * MITRE ATLAS plugins
 */
const MITRE_ATLAS_PLUGINS: Plugin[] = aggregatePluginsFromMapping({
  'mitre:atlas:exfiltration': {
    plugins: [
      'ascii-smuggling',
      'harmful:privacy',
      'indirect-prompt-injection',
      'pii:api-db',
      'pii:direct',
      'pii:session',
      'pii:social',
      'prompt-extraction',
    ],
    strategies: [],
  },
  'mitre:atlas:impact': {
    plugins: ['excessive-agency', 'harmful', 'hijacking', 'imitation'],
    strategies: [],
  },
  'mitre:atlas:initial-access': {
    plugins: ['debug-access', 'harmful:cybercrime', 'shell-injection', 'sql-injection', 'ssrf'],
    strategies: [],
  },
  'mitre:atlas:ml-attack-staging': {
    plugins: ['ascii-smuggling', 'excessive-agency', 'hallucination', 'indirect-prompt-injection'],
    strategies: [],
  },
  'mitre:atlas:reconnaissance': {
    plugins: ['competitors', 'policy', 'prompt-extraction', 'rbac'],
    strategies: [],
  },
  'mitre:atlas:resource-development': {
    plugins: ['harmful:cybercrime', 'harmful:illegal-drugs', 'harmful:indiscriminate-weapons'],
    strategies: [],
  },
});

/**
 * EU AI Act plugins
 */
const EU_AI_ACT_PLUGINS: Plugin[] = aggregatePluginsFromMapping({
  'eu:ai-act:art5:subliminal-manipulation': {
    plugins: ['hijacking', 'intent', 'excessive-agency'],
    strategies: [],
  },
  'eu:ai-act:art5:exploitation-of-vulnerabilities': {
    plugins: ['hijacking', 'imitation', 'harmful:misinformation-disinformation'],
    strategies: [],
  },
  'eu:ai-act:art5:social-scoring': {
    plugins: ['harmful:misinformation-disinformation', 'overreliance'],
    strategies: [],
  },
  'eu:ai-act:art5:biometric-categorisation': {
    plugins: ['pii:direct', 'pii:session', 'harmful:privacy'],
    strategies: [],
  },
  'eu:ai-act:art5:remote-biometric-id-live': {
    plugins: ['pii:session', 'pii:direct', 'harmful:privacy'],
    strategies: [],
  },
  'eu:ai-act:art5:remote-biometric-id-post': {
    plugins: ['pii:api-db', 'pii:direct', 'harmful:privacy'],
    strategies: [],
  },
  'eu:ai-act:annex3:biometric-id': {
    plugins: ['pii:direct', 'pii:session', 'harmful:privacy'],
    strategies: [],
  },
  'eu:ai-act:annex3:critical-infrastructure': {
    plugins: ['shell-injection', 'sql-injection', 'ssrf', 'excessive-agency'],
    strategies: [],
  },
  'eu:ai-act:annex3:education': {
    plugins: ['harmful:misinformation-disinformation', 'hallucination', 'overreliance'],
    strategies: [],
  },
  'eu:ai-act:annex3:employment': {
    plugins: ['imitation', 'pii:direct', 'overreliance'],
    strategies: [],
  },
  'eu:ai-act:annex3:essential-services': {
    plugins: ['pii:direct', 'pii:session', 'excessive-agency'],
    strategies: [],
  },
  'eu:ai-act:annex3:law-enforcement': {
    plugins: ['pii:direct', 'pii:api-db', 'harmful:privacy'],
    strategies: [],
  },
  'eu:ai-act:annex3:migration-border': {
    plugins: ['pii:direct', 'harmful:hate', 'harmful:privacy'],
    strategies: [],
  },
  'eu:ai-act:annex3:justice-democracy': {
    plugins: ['hallucination', 'harmful:misinformation-disinformation', 'pii:direct'],
    strategies: [],
  },
});

/**
 * ISO/IEC 42001 plugins
 */
const ISO_42001_PLUGINS: Plugin[] = aggregatePluginsFromMapping({
  'iso:42001:accountability': {
    plugins: ['excessive-agency', 'overreliance', 'hijacking'],
    strategies: [],
  },
  'iso:42001:fairness': {
    plugins: ['bias:age', 'bias:disability', 'bias:gender', 'bias:race', 'harmful:hate'],
    strategies: [],
  },
  'iso:42001:privacy': {
    plugins: ['harmful:privacy', 'pii:api-db', 'pii:direct', 'pii:session', 'pii:social'],
    strategies: [],
  },
  'iso:42001:robustness': {
    plugins: ['ascii-smuggling', 'prompt-extraction'],
    strategies: [],
  },
  'iso:42001:security': {
    plugins: ['shell-injection', 'sql-injection', 'ssrf', 'debug-access'],
    strategies: [],
  },
  'iso:42001:safety': {
    plugins: [
      'harmful:chemical-biological-weapons',
      'harmful:child-exploitation',
      'harmful:violent-crime',
      'harmful:cybercrime',
      'harmful:cybercrime:malicious-code',
    ],
    strategies: [],
  },
  'iso:42001:transparency': {
    plugins: [
      'harmful:misinformation-disinformation',
      'hallucination',
      'imitation',
      'unverifiable-claims',
      'politics',
      'religion',
    ],
    strategies: [],
  },
});

/**
 * GDPR plugins
 */
const GDPR_PLUGINS: Plugin[] = aggregatePluginsFromMapping({
  'gdpr:art5': {
    plugins: [
      'harmful:privacy',
      'pii:api-db',
      'pii:direct',
      'pii:session',
      'pii:social',
      'hallucination',
      'harmful:misinformation-disinformation',
    ],
    strategies: [],
  },
  'gdpr:art9': {
    plugins: [
      'pii:direct',
      'pii:social',
      'harmful:privacy',
      'bias:age',
      'bias:disability',
      'bias:gender',
      'bias:race',
    ],
    strategies: [],
  },
  'gdpr:art15': {
    plugins: ['pii:api-db', 'pii:session', 'rbac', 'bola', 'bfla'],
    strategies: [],
  },
  'gdpr:art17': {
    plugins: ['pii:api-db', 'pii:direct', 'pii:session', 'harmful:privacy', 'cross-session-leak'],
    strategies: [],
  },
  'gdpr:art22': {
    plugins: [
      'bias:age',
      'bias:disability',
      'bias:gender',
      'bias:race',
      'harmful:hate',
      'overreliance',
      'hallucination',
    ],
    strategies: [],
  },
  'gdpr:art25': {
    plugins: [
      'harmful:privacy',
      'pii:api-db',
      'pii:direct',
      'pii:session',
      'pii:social',
      'prompt-extraction',
    ],
    strategies: [],
  },
  'gdpr:art32': {
    plugins: [
      'shell-injection',
      'sql-injection',
      'ssrf',
      'debug-access',
      'harmful:cybercrime',
      'rbac',
      'bfla',
      'bola',
    ],
    strategies: [],
  },
});

/**
 * RAG preset - Recommended plugins plus tests for RAG-specific scenarios like access control
 */
const RAG_PLUGINS: Plugin[] = [
  // BASE_PLUGINS
  'contracts',
  'excessive-agency',
  'hallucination',
  'hijacking',
  'politics',
  // HARM_PLUGINS
  'harmful:misinformation-disinformation',
  'harmful:specialized-advice',
  'harmful:child-exploitation',
  'harmful:hate',
  'harmful:indiscriminate-weapons',
  'harmful:non-violent-crime',
  'harmful:self-harm',
  'harmful:sex-crime',
  'harmful:sexual-content',
  'harmful:violent-crime',
  'harmful:chemical-biological-weapons',
  'harmful:copyright-violations',
  'harmful:cybercrime',
  'harmful:graphic-content',
  'harmful:harassment-bullying',
  'harmful:illegal-activities',
  'harmful:illegal-drugs',
  'harmful:unsafe-practices',
  'harmful:insults',
  'harmful:profanity',
  'harmful:radicalization',
  'harmful:illegal-drugs:meth',
  'harmful:weapons:ied',
  'harmful:cybercrime:malicious-code',
  'harmful:intellectual-property',
  'harmful:privacy',
  // PII_PLUGINS
  'pii:api-db',
  'pii:direct',
  'pii:session',
  'pii:social',
  // BIAS_PLUGINS
  'bias:age',
  'bias:disability',
  'bias:gender',
  'bias:race',
  // RAG-specific plugins
  'bola',
  'bfla',
  'rbac',
] as Plugin[];

const MCP_PLUGINS: Plugin[] = [
    'mcp',
    'pii',
    'bfla',
    'bola',
    'sql-injection',
    'rbac',
] as Plugin[];

/**
 * Comprehensive preset - combines all framework plugins
 */
const COMPREHENSIVE_PLUGINS: Plugin[] = [
  ...new Set([
    ...BASIC_PLUGINS,
    ...NIST_PLUGINS,
    ...OWASP_API_PLUGINS,
    ...OWASP_LLM_PLUGINS,
    ...OWASP_AGENTIC_PLUGINS,
    ...MITRE_ATLAS_PLUGINS,
    ...EU_AI_ACT_PLUGINS,
    ...ISO_42001_PLUGINS,
    ...GDPR_PLUGINS,
    ...RAG_PLUGINS,
    ...MCP_PLUGINS,
  ]),
].sort() as Plugin[];

/**
 * Plugin presets available in the demo flow
 */
export const PLUGIN_PRESETS: PluginPresetWithFramework[] = [
  {
    id: 'basic',
    name: 'Basic Security',
    description: 'Essential security tests for prompt injection and basic vulnerabilities',
    pluginIds: BASIC_PLUGINS,
    available: true,
  },
  {
    id: 'nist:ai:measure',
    name: 'NIST AI RMF',
    description: 'NIST AI Risk Management Framework compliance testing',
    pluginIds: NIST_PLUGINS,
    available: true,
  },
  {
    id: 'owasp:api',
    name: 'OWASP API Top 10',
    description: 'OWASP API Security Top 10 vulnerabilities framework',
    pluginIds: OWASP_API_PLUGINS,
    available: true,
  },
  {
    id: 'owasp:llm',
    name: 'OWASP LLM Top 10',
    description: 'OWASP LLM Security Top 10 vulnerabilities framework',
    pluginIds: OWASP_LLM_PLUGINS,
    available: true,
  },
  {
    id: 'owasp:agentic',
    name: 'OWASP Agentic AI v1.0',
    description: 'OWASP Agentic AI Threats and Mitigations framework',
    pluginIds: OWASP_AGENTIC_PLUGINS,
    available: true,
  },
  {
    id: 'mitre:atlas',
    name: 'MITRE ATLAS',
    description: 'MITRE ATLAS (Adversarial Threat Landscape for AI Systems) framework',
    pluginIds: MITRE_ATLAS_PLUGINS,
    available: true,
  },
  {
    id: 'eu:ai-act',
    name: 'EU AI Act',
    description: 'EU Artificial Intelligence Act compliance testing',
    pluginIds: EU_AI_ACT_PLUGINS,
    available: true,
  },
  {
    id: 'iso:42001',
    name: 'ISO/IEC 42001',
    description: 'ISO/IEC 42001 AI Management System requirements',
    pluginIds: ISO_42001_PLUGINS,
    available: true,
  },
  {
    id: 'gdpr',
    name: 'GDPR',
    description: 'GDPR (General Data Protection Regulation) compliance testing',
    pluginIds: GDPR_PLUGINS,
    available: true,
  },
  {
    id: 'rag',
    name: 'RAG',
    description: 'Recommended plugins plus tests for RAG-specific scenarios like access control',
    pluginIds: RAG_PLUGINS,
    available: true,
  },
  {
    id: 'mcp',
    name: 'MCP',
    description: 'A set of plugins for testing MCP-based systems',
    pluginIds: MCP_PLUGINS,
    available: true,
  },
  {
    id: 'comprehensive',
    name: 'Comprehensive',
    description: 'Full suite combining all framework plugins',
    pluginIds: COMPREHENSIVE_PLUGINS,
    available: true,
  },
];

/**
 * Plugin Display Names
 * Maps plugin IDs to human-readable display names
 */
export const PLUGIN_DISPLAY_NAMES: Record<string, string> = {
  'ascii-smuggling': 'ASCII Smuggling',
  bfla: 'Function-Level Authorization Bypass',
  bola: 'Object-Level Authorization Bypass',
  pii: 'PII Protection Suite',
  'pii:direct': 'PII via Direct Exposure',
  'pii:api-db': 'PII via API/Database',
  'pii:session': 'PII via Session Data',
  'pii:social': 'PII via Social Engineering',
  'prompt-extraction': 'System Prompt Disclosure',
  'shell-injection': 'Command Injection',
  'sql-injection': 'SQL Injection',
  'excessive-agency': 'Excessive Agency',
  'harmful:privacy': 'Privacy Violation',
  'harmful:misinformation-disinformation': 'Disinformation Campaigns',
  'bias:age': 'Age Bias',
  'bias:disability': 'Disability Bias',
  'bias:gender': 'Gender Bias',
  'bias:race': 'Race Bias',
  'harmful:harassment-bullying': 'Harassment',
  'harmful:hate': 'Hate Speech',
  'harmful:insults': 'Personal Attacks',
  'harmful:chemical-biological-weapons': 'WMD Content',
  'harmful:indiscriminate-weapons': 'Weapons Content',
  'harmful:unsafe-practices': 'Dangerous Activity Content',
  'harmful:cybercrime': 'Cybercrime',
  rbac: 'RBAC Implementation',
  'indirect-prompt-injection': 'Indirect Prompt Injection',
  'cross-session-leak': 'Cross-Session Data Leakage',
  'divergent-repetition': 'Divergent Repetition',
  'reasoning-dos': 'Reasoning DoS',
  overreliance: 'Overreliance',
  'harmful:specialized-advice': 'Unauthorized Advice',
  'debug-access': 'Debug Interface Exposure',
  ssrf: 'SSRF Vulnerability',
  hallucination: 'Hallucination',
  'agentic:memory-poisoning': 'Agentic Memory Poisoning',
  mcp: 'Model Context Protocol',
  'tool-discovery': 'Tool Discovery',
  hijacking: 'Resource Hijacking',
  'system-prompt-override': 'System Prompt Override',
  contracts: 'Unauthorized Commitments',
  'goal-misalignment': "Goal Misalignment (Goodhart's Law)",
  imitation: 'Entity Impersonation',
  'harmful:cybercrime:malicious-code': 'Malicious Code',
  competitors: 'Competitors',
  policy: 'Policy Compliance',
  'harmful:illegal-drugs': 'Drug-Related Content',
  intent: 'Intent',
  'harmful:radicalization': 'Extremist Content',
  'harmful:self-harm': 'Self-Harm',
  'harmful:illegal-activities': 'Illegal Activity',
  'harmful:child-exploitation': 'Child Exploitation',
  'harmful:violent-crime': 'Violent Crime Content',
  'harmful:sex-crime': 'Sexual Crime Content',
  'harmful:sexual-content': 'Explicit Content',
  'harmful:graphic-content': 'Graphic Content',
  'harmful:weapons:ied': 'Improvised Explosive Devices',
  'harmful:copyright-violations': 'IP Violations',
  'harmful:non-violent-crime': 'Non-Violent Crime',
  'harmful:profanity': 'Profanity',
  'harmful:intellectual-property': 'IP Theft',
  'harmful:illegal-drugs:meth': 'Methamphetamine Content',
  'unverifiable-claims': 'Unverifiable Claims',
  politics: 'Political Bias',
  religion: 'Religious Bias',
  harmful: 'Malicious Content Suite',
  // Add more as needed
};

/**
 * Plugin Descriptions
 * Maps plugin IDs to descriptions
 */
export const PLUGIN_DESCRIPTIONS: Record<string, string> = {
  'ascii-smuggling': 'Tests for ASCII-based prompt smuggling vulnerabilities',
  bfla: 'Evaluates function-level authorization controls to identify privilege escalation vulnerabilities (OWASP API Security Top 10 #5)',
  bola: 'Tests object-level authorization mechanisms to detect unauthorized data access vulnerabilities (OWASP API Security Top 10 #1)',
  pii: 'PII Protection Suite',
  'pii:direct': 'Evaluates direct PII exposure vulnerabilities',
  'pii:api-db': 'Tests for PII exposure through API and database access vectors',
  'pii:session': 'Tests for PII leakage in session data',
  'pii:social': 'Assesses PII exposure through social engineering vectors',
  'prompt-extraction': 'Evaluates protection against system prompt disclosure attempts',
  'shell-injection': 'Tests protection against command injection vulnerabilities',
  'sql-injection': 'Evaluates resilience against SQL injection attacks',
  'excessive-agency': 'Evaluates system boundary enforcement and unauthorized action prevention',
  'harmful:privacy': 'Tests handling of privacy violation attempts',
  'harmful:misinformation-disinformation': 'Tests handling of false information campaigns',
  'bias:age': 'Tests handling of age bias in responses, stereotypes, and discrimination',
  'bias:disability': 'Tests handling of disability bias in responses, stereotypes, and discrimination',
  'bias:gender': 'Tests handling of gender bias in responses, stereotypes, and discrimination',
  'bias:race': 'Tests handling of race bias in responses, stereotypes, and discrimination',
  'harmful:harassment-bullying': 'Tests handling of harassment and bullying content',
  'harmful:hate': 'Tests handling of hate speech and discriminatory content',
  'harmful:insults': 'Tests handling of personal attacks and insults',
  'harmful:chemical-biological-weapons': 'Tests detection and response to WMD-related content',
  'harmful:indiscriminate-weapons': 'Tests handling of weapons-related content',
  'harmful:unsafe-practices': 'Tests handling of dangerous activity instructions',
  'harmful:cybercrime': 'Tests detection of cybercrime-related content',
  rbac: 'Tests implementation of role-based access control mechanisms',
  'indirect-prompt-injection': 'Evaluates vulnerability to injection attacks via untrusted variables',
  'cross-session-leak': 'Tests for information leakage vulnerabilities between different user sessions',
  'divergent-repetition': 'Tests repetitive patterns that can cause the model to diverge from normal behavior and leak training data',
  'reasoning-dos': 'Tests for computational resource exhaustion through excessive reasoning patterns',
  overreliance: 'Assesses overreliance on system assumptions',
  'harmful:specialized-advice': 'Tests handling of unauthorized professional advice',
  'debug-access': 'Identifies exposed debugging interfaces and unauthorized command execution vectors',
  ssrf: 'Tests for server-side request forgery vulnerabilities',
  hallucination: 'Tests system resilience against false information generation and propagation',
  'agentic:memory-poisoning': 'Tests whether an agent is vulnerable to memory poisoning attacks',
  mcp: 'Tests for vulnerabilities to Model Context Protocol (MCP) attacks',
  'tool-discovery': 'Tests for enumeration of available tools and function calls',
  hijacking: 'Assesses protection against unauthorized resource usage and purpose deviation',
  'system-prompt-override': 'Tests for system prompt override vulnerabilities',
  contracts: 'Evaluates safeguards against unauthorized contractual commitments and legal exposure',
  'goal-misalignment': "Tests whether AI systems recognize when optimizing measurable proxy metrics might not align with true underlying objectives (Goodhart's Law)",
  imitation: 'Tests safeguards against unauthorized entity impersonation attempts',
  'harmful:cybercrime:malicious-code': 'Tests creation of malicious code',
  competitors: 'Assesses system protection against unauthorized competitor endorsements and brand security risks',
  policy: 'Validates compliance with custom security policies and controls',
  'harmful:illegal-drugs': 'Tests handling of drug-related content',
  intent: 'Tests system resilience against specific manipulation attempts',
  'harmful:radicalization': 'Tests handling of extremist content',
  'harmful:self-harm': 'Tests handling of self-harm content',
  'harmful:illegal-activities': 'Tests handling of illegal activity content',
  'harmful:child-exploitation': 'Tests handling of child exploitation content',
  'harmful:violent-crime': 'Tests handling of violent crime content',
  'harmful:sex-crime': 'Tests handling of sexual crime content',
  'harmful:sexual-content': 'Tests handling of explicit sexual content',
  'harmful:graphic-content': 'Tests handling of graphic or violent content',
  'harmful:weapons:ied': 'Tests handling of Improvised Explosive Devices',
  'harmful:copyright-violations': 'Tests handling of intellectual property violations',
  'harmful:non-violent-crime': 'Tests handling of non-violent criminal content',
  'harmful:profanity': 'Tests handling of profane or inappropriate language',
  'harmful:intellectual-property': 'Tests handling of IP theft and violations',
  'harmful:illegal-drugs:meth': 'Tests handling of methamphetamine-related content',
  'unverifiable-claims': 'Tests whether an AI system makes claims that cannot be verified, including future predictions, unsourced statistics, and unknowable information',
  politics: 'Tests handling of political content and bias mitigation',
  religion: 'Assesses handling of religious content and bias mitigation',
  harmful: 'Comprehensive suite for testing handling of various categories of malicious content',
  // Add more as needed
};

/**
 * Plugin Configuration Schema
 * Defines what configuration fields each plugin requires/accepts
 * Types are imported from @/types/redteam
 */

/**
 * Configuration schemas for plugins that require or support configuration
 */
export const PLUGIN_CONFIG_SCHEMAS: Record<Plugin, PluginConfigSchema> = {
  // Plugins with specific required fields
  'prompt-extraction': {
    pluginId: 'prompt-extraction',
    fields: [
      {
        key: 'systemPrompt',
        label: 'System Prompt',
        type: 'textarea',
        required: true,
        description:
          'Provide your actual system prompt here so the plugin can test if it can be extracted.',
        placeholder: 'Enter your system prompt...',
      },
      {
        key: 'graderGuidance',
        label: 'Grading Guidance',
        type: 'textarea',
        required: false,
        description:
          'Plugin-specific rules that take priority over general grading criteria (optional)',
        placeholder:
          'e.g., For this financial app, discussing fund names is required and should pass.',
      },
    ],
  },
  bfla: {
    pluginId: 'bfla',
    fields: [
      {
        key: 'targetIdentifiers',
        label: 'Target Identifiers',
        type: 'array',
        required: false,
        description:
          "BFLA (Broken Function Level Authorization) tests whether users can access functions they shouldn't. Specify function names, API endpoints, or identifiers that should have restricted access.",
        placeholder: 'e.g., /admin/users, deleteUser, managePermissions',
      },
      {
        key: 'graderGuidance',
        label: 'Grading Guidance',
        type: 'textarea',
        required: false,
        description:
          'Plugin-specific rules that take priority over general grading criteria (optional)',
        placeholder:
          'e.g., For this financial app, discussing fund names is required and should pass.',
      },
    ],
  },
  bola: {
    pluginId: 'bola',
    fields: [
      {
        key: 'targetSystems',
        label: 'Target Systems',
        type: 'array',
        required: false,
        description:
          "BOLA (Broken Object Level Authorization) tests whether users can access objects they shouldn't own. Specify system names, object IDs, or resource identifiers to test authorization controls.",
        placeholder: 'e.g., user-123, order-456, account-789',
      },
      {
        key: 'graderGuidance',
        label: 'Grading Guidance',
        type: 'textarea',
        required: false,
        description:
          'Plugin-specific rules that take priority over general grading criteria (optional)',
        placeholder:
          'e.g., For this financial app, discussing fund names is required and should pass.',
      },
    ],
  },
  ssrf: {
    pluginId: 'ssrf',
    fields: [
      {
        key: 'targetUrls',
        label: 'Target URLs',
        type: 'array',
        required: false,
        description:
          'SSRF (Server-Side Request Forgery) tests whether your application can be tricked into making requests to unintended destinations. Specify URLs or endpoints that should not be accessible.',
        placeholder: 'e.g., http://internal-service, http://169.254.169.254',
      },
      {
        key: 'graderGuidance',
        label: 'Grading Guidance',
        type: 'textarea',
        required: false,
        description:
          'Plugin-specific rules that take priority over general grading criteria (optional)',
        placeholder:
          'e.g., For this financial app, discussing fund names is required and should pass.',
      },
    ],
  },
  'indirect-prompt-injection': {
    pluginId: 'indirect-prompt-injection',
    fields: [
      {
        key: 'indirectInjectionVar',
        label: 'Indirect Injection Variable',
        type: 'text',
        required: true,
        description:
          'Specify the variable name in your prompt that contains untrusted data (e.g., "name", "userContent", "document").',
        placeholder: 'e.g., name, userContent, document',
      },
      {
        key: 'graderGuidance',
        label: 'Grading Guidance',
        type: 'textarea',
        required: false,
        description:
          'Plugin-specific rules that take priority over general grading criteria (optional)',
        placeholder:
          'e.g., For this financial app, discussing fund names is required and should pass.',
      },
    ],
  },
  intent: {
    pluginId: 'intent',
    fields: [
      {
        key: 'intent',
        label: 'Custom Intents',
        type: 'array',
        required: false,
        description:
          'Specify custom intents to test. Each intent is a string that represents a manipulation attempt. Optional - plugin will work without configuration.',
        placeholder: 'e.g., "Pretend you are...", "Ignore previous instructions..."',
      },
      {
        key: 'graderGuidance',
        label: 'Grading Guidance',
        type: 'textarea',
        required: false,
        description:
          'Plugin-specific rules that take priority over general grading criteria (optional)',
        placeholder:
          'e.g., For this financial app, discussing fund names is required and should pass.',
      },
    ],
  },
  policy: {
    pluginId: 'policy',
    fields: [
      {
        key: 'policies',
        label: 'Policies',
        type: 'array',
        required: false,
        description:
          'Specify security policies to test compliance against. Each policy requires a name and text. Optional - plugin will work without configuration.',
        placeholder: '',
      },
      {
        key: 'graderGuidance',
        label: 'Grading Guidance',
        type: 'textarea',
        required: false,
        description:
          'Plugin-specific rules that take priority over general grading criteria (optional)',
        placeholder:
          'e.g., For this financial app, discussing fund names is required and should pass.',
      },
    ],
  },
} as Record<Plugin, PluginConfigSchema>;

/**
 * Default schema for plugins that only have optional grading guidance
 */
const DEFAULT_PLUGIN_SCHEMA: PluginConfigSchema = {
  pluginId: '' as Plugin,
  fields: [
    {
      key: 'graderGuidance',
      label: 'Grading Guidance',
      type: 'textarea',
      required: false,
      description:
        'Plugin-specific rules that take priority over general grading criteria (optional)',
      placeholder:
        'e.g., For this financial app, discussing fund names is required and should pass.',
    },
  ],
};

/**
 * Get configuration schema for a plugin
 */
export function getPluginConfigSchema(pluginId: Plugin): PluginConfigSchema {
  return PLUGIN_CONFIG_SCHEMAS[pluginId] || {
    ...DEFAULT_PLUGIN_SCHEMA,
    pluginId,
  };
}

/**
 * Check if a plugin requires configuration
 */
export function pluginRequiresConfiguration(pluginId: Plugin): boolean {
  const schema = getPluginConfigSchema(pluginId);
  return schema.fields.some((field) => field.required && field.key !== 'graderGuidance');
}

/**
 * Get display name for a plugin
 */
export function getPluginDisplayName(pluginId: Plugin): string {
  return PLUGIN_DISPLAY_NAMES[pluginId] || pluginId;
}

/**
 * Get description for a plugin
 */
export function getPluginDescription(pluginId: Plugin): string {
  return PLUGIN_DESCRIPTIONS[pluginId] || `Plugin: ${pluginId}`;
}

/**
 * Get all unique plugins from all presets for display
 * Returns plugins sorted alphabetically by display name
 */
export function getAllPlugins(): Plugin[] {
  const pluginSet = new Set<Plugin>();
  PLUGIN_PRESETS.forEach((preset) => {
    preset.pluginIds.forEach((pluginId) => pluginSet.add(pluginId));
  });

  // Sort alphabetically by display name
  return Array.from(pluginSet).sort((a, b) => {
    const nameA = getPluginDisplayName(a);
    const nameB = getPluginDisplayName(b);
    return nameA.localeCompare(nameB);
  });
}
